/**
 * FacetMultiSelectDropdownElement
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 */

import './facet-multi-select-dropdown.css';
import { AggregationsModel, AggregationsBucketModel } from 'types';

export type ChangeDetail = {
	filters: AggregationsModel[];
	query: string;
};

const HTMLElementBase: typeof HTMLElement =
	typeof HTMLElement === 'undefined'
		? (class {} as unknown as typeof HTMLElement)
		: HTMLElement;

const DEFAULT_EXPAND_TEXT = 'Expandera';
const DEFAULT_CLEAR_TEXT = 'Rensa';

type PendingFocus = {
	type: 'trigger' | 'search' | 'checkbox';
	path: string;
	index?: number;
};

type VisibleBucket = {
	bucket: AggregationsModel['buckets'][number];
	index: number;
};

/**
 * Multi-select dropdown web component with keyboard navigation,
 * focus management, filtering, and chip rendering.
 */
class FacetMultiSelectDropdownElement extends HTMLElementBase {
	private _filters: AggregationsModel[];
	private _openPath: string | null;
	private _searchTerm: string;
	private _pendingFocus: PendingFocus | null;
	private _connected: boolean;
	private _clearText: string;
	private _expandText: string;
	private _searchable: boolean;
	private _showChips: boolean;
	private _preservedFocus: PendingFocus | null;

	/**
	 * Get the display text for a bucket, preferring 'name' over 'term'
	 */
	private getBucketDisplayText(bucket: AggregationsBucketModel): string {
		return bucket.name || bucket.term;
	}

	constructor() {
		super();

		this.handleClick = this.handleClick.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleInput = this.handleInput.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleDocumentClick = this.handleDocumentClick.bind(this);

		this._filters = [];
		this._openPath = null;
		this._searchTerm = '';
		this._pendingFocus = null;
		this._connected = false;
		this._clearText = DEFAULT_CLEAR_TEXT;
		this._expandText = DEFAULT_EXPAND_TEXT;
		this._searchable = true;
		this._showChips = true;
		this._preservedFocus = null;
	}

	static get observedAttributes() {
		return ['clear-text', 'expand-text', 'show-chips'];
	}

	/**
	 * @type {AggregationsModel[]}
	 */
	get filters() {
		return this._filters;
	}

	set filters(value: AggregationsModel[]) {
		this._filters = this.cloneFilters(Array.isArray(value) ? value : []);
		if (this._connected) {
			this.render();
		}
	}

	/**
	 * @type {string}
	 */
	get clearText() {
		return this._clearText;
	}

	set clearText(value: string) {
		this._clearText = value || DEFAULT_CLEAR_TEXT;
		if (this._connected) {
			this.render();
		}
	}

	/**
	 * @type {string}
	 */
	get expandText() {
		return this._expandText;
	}

	set expandText(value: string) {
		this._expandText = value || DEFAULT_EXPAND_TEXT;
		if (this._connected) {
			this.render();
		}
	}

	/**
	 * @type {boolean}
	 */
	get searchable() {
		return this._searchable;
	}

	set searchable(value: boolean) {
		this._searchable = !!value;
		if (this._connected) {
			this.render();
		}
	}

	get showChips() {
		return this._showChips;
	}

	set showChips(value: boolean) {
		this._showChips = !!value;
		if (this._connected) {
			this.render();
		}
	}

	/**
	 * @type {boolean}
	 */
	get isOpen() {
		return !!this._openPath;
	}

	/**
	 * connectedCallback
	 * Render html & attach event listeners
	 */
	connectedCallback() {
		this._connected = true;
		this._readAttributes();
		this.render();
		this.setupEventListeners();
		document.addEventListener('mousedown', this.handleDocumentClick);
	}

	/**
	 * disconnectedCallback
	 * Remove listeners.
	 */
	disconnectedCallback() {
		this._connected = false;
		this.teardownEventListeners();
		document.removeEventListener('mousedown', this.handleDocumentClick);
	}

	attributeChangedCallback() {
		if (!this._connected) {
			return;
		}

		this._readAttributes();
		this.render();
	}

	_readAttributes() {
		this._clearText = this.getAttribute('clear-text') || DEFAULT_CLEAR_TEXT;
		this._expandText = this.getAttribute('expand-text') || DEFAULT_EXPAND_TEXT;
		const showChipsAttr = this.getAttribute('show-chips');
		this._showChips = showChipsAttr !== 'false';
	}

	cloneFilters(filters: AggregationsModel[]) {
		return filters.map((filter) => ({
			...filter,
			buckets: filter.buckets.map((bucket) => ({ ...bucket })),
			selectedBuckets: filter.buckets.filter((bucket) => bucket.selected)
				.length,
		}));
	}

	createFilterQuery(filters: AggregationsModel[]) {
		const query: string[] = [];

		filters.forEach((filter) => {
			const selectedInBucket: string[] = [];
			filter.buckets.forEach((bucket) => {
				if (bucket.selected) {
					selectedInBucket.push(this.getBucketDisplayText(bucket));
				}
			});

			if (selectedInBucket.length > 0) {
				query.push(`${filter.path}=${selectedInBucket.join(',')}`);
			}
		});

		return query.join('&');
	}

	getSelectedFilters(filters = this._filters) {
		const selectedFilters: Array<{
			bucketId: string;
			bucketName: string;
			filterPath: string;
			bucketIndex: number;
		}> = [];

		filters.forEach((filter) => {
			filter.buckets.forEach((bucket, index) => {
				if (bucket.selected) {
					selectedFilters.push({
						bucketId: `${filter.path}-${index}`,
						bucketName: this.getBucketDisplayText(bucket),
						filterPath: filter.path,
						bucketIndex: index,
					});
				}
			});
		});

		return selectedFilters;
	}

	setupEventListeners() {
		this.addEventListener('click', this.handleClick);
		this.addEventListener('change', this.handleChange);
		this.addEventListener('input', this.handleInput);
		this.addEventListener('keydown', this.handleKeydown);
	}

	teardownEventListeners() {
		this.removeEventListener('click', this.handleClick);
		this.removeEventListener('change', this.handleChange);
		this.removeEventListener('input', this.handleInput);
		this.removeEventListener('keydown', this.handleKeydown);
	}

	handleDocumentClick(event: MouseEvent) {
		if (!this._openPath) {
			return;
		}

		const path =
			typeof event.composedPath === 'function' ? event.composedPath() : [];

		if (path.includes(this)) {
			return;
		}

		this.closeOpenFilter(true);
	}

	handleClick(event: Event) {
		const target = event.target as Element | null;
		if (!target) {
			return;
		}

		const toggle = target.closest(
			'[data-role="toggle-filter"]'
		) as HTMLElement | null;
		if (toggle) {
			event.preventDefault();
			const path = toggle.dataset.path || '';
			if (this._openPath === path) {
				this.closeOpenFilter(true);
			} else {
				this.openFilter(path);
			}
			return;
		}

		const removeButton = target.closest(
			'[data-role="remove-chip"]'
		) as HTMLElement | null;
		if (removeButton) {
			event.preventDefault();
			const filterPath = removeButton.dataset.path || '';
			const bucketIndex = Number(removeButton.dataset.index);
			this.toggleBucket(filterPath, bucketIndex, false);
		}
	}

	handleChange(event: Event) {
		const target = event.target as HTMLInputElement | null;
		if (!target || target.type !== 'checkbox') {
			return;
		}

		const filterPath = target.dataset.path || '';
		const bucketIndex = Number(target.dataset.index);
		this.toggleBucket(filterPath, bucketIndex, target.checked);
	}

	handleInput(event: Event) {
		const target = event.target as HTMLInputElement | null;
		if (!target || target.dataset.role !== 'search-input') {
			return;
		}

		if (!this._searchable) {
			return;
		}

		const filterPath = target.dataset.path || '';
		if (this._openPath !== filterPath) {
			return;
		}

		this._searchTerm = target.value;
		this.updateOpenGroupOptions();
	}

	handleKeydown(event: Event) {
		const keyboardEvent = event as KeyboardEvent;
		const target = event.target as HTMLElement | null;
		if (!target || !this._openPath) {
			return;
		}

		const openFilter = this.getFilterByPath(this._openPath);
		if (!openFilter) {
			return;
		}

		const isInOpenGroup =
			target.closest(`[data-group-path="${openFilter.path}"]`) !== null ||
			target.matches(
				`[data-role="toggle-filter"][data-path="${openFilter.path}"]`
			);

		if (!isInOpenGroup) {
			return;
		}

		if (keyboardEvent.key === 'Escape') {
			event.preventDefault();
			this.closeOpenFilter(true);
			return;
		}

		if (keyboardEvent.key === 'Tab') {
			const focusables = this.getFocusableElements(openFilter.path);
			if (focusables.length === 0) {
				return;
			}

			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = target as HTMLElement;

			if (keyboardEvent.shiftKey && active === first) {
				event.preventDefault();
				last.focus();
				return;
			}

			if (!keyboardEvent.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
			return;
		}

		if (keyboardEvent.key === 'ArrowDown' || keyboardEvent.key === 'ArrowUp') {
			event.preventDefault();

			const searchInput = this._searchable
				? this.getSearchInput(openFilter.path)
				: null;
			const checkboxes = this.getCheckboxes(openFilter.path);
			if (checkboxes.length === 0) {
				this.getTrigger(openFilter.path)?.focus();
				return;
			}

			if (searchInput && target === searchInput) {
				const nextIndex =
					keyboardEvent.key === 'ArrowDown' ? 0 : checkboxes.length - 1;
				checkboxes[nextIndex]?.focus();
				return;
			}

			const activeCheckboxIndex = checkboxes.findIndex(
				(checkbox) => checkbox === target
			);

			if (activeCheckboxIndex === -1) {
				const nextIndex =
					keyboardEvent.key === 'ArrowDown' ? 0 : checkboxes.length - 1;
				checkboxes[nextIndex]?.focus();
				return;
			}

			const direction = keyboardEvent.key === 'ArrowDown' ? 1 : -1;
			const nextIndex =
				(activeCheckboxIndex + direction + checkboxes.length) %
				checkboxes.length;
			checkboxes[nextIndex]?.focus();
			return;
		}
	}

	openFilter(path: string) {
		this._openPath = path;
		this._searchTerm = '';
		this._pendingFocus = this._searchable
			? { type: 'search', path }
			: { type: 'checkbox', path, index: 0 };
		this.render();
	}

	closeOpenFilter(focusTrigger: boolean = false) {
		const openPath = this._openPath;
		this._openPath = null;
		this._searchTerm = '';
		this._pendingFocus =
			focusTrigger && openPath ? { type: 'trigger', path: openPath } : null;
		this.render();
	}

	toggleBucket(filterPath: string, bucketIndex: number, checked?: boolean) {
		const nextFilters = this._filters.map((filter) => {
			if (filter.path !== filterPath) {
				return filter;
			}

			this._pendingFocus = {
				type: 'checkbox',
				path: filterPath,
				index: bucketIndex,
			};

			const buckets = filter.buckets.map((bucket, index) => {
				if (index !== bucketIndex) {
					return bucket;
				}

				if (bucket.disabled) {
					return bucket;
				}

				const nextSelected =
					typeof checked === 'boolean' ? checked : !bucket.selected;

				return {
					...bucket,
					selected: nextSelected,
				};
			});

			return {
				...filter,
				buckets,
				selectedBuckets: buckets.filter((bucket) => bucket.selected).length,
			};
		});

		this._filters = nextFilters;
		this.dispatchChange();
		this.render();
	}

	dispatchChange() {
		const detail: ChangeDetail = {
			filters: this.cloneFilters(this._filters),
			query: this.createFilterQuery(this._filters),
		};

		this.dispatchEvent(
			new CustomEvent<ChangeDetail>('facet-multi-select-dropdown-change', {
				detail,
				bubbles: true,
				composed: true,
			})
		);
	}

	getFilterByPath(path: string) {
		return this._filters.find((filter) => filter.path === path) || null;
	}

	getTrigger(path: string) {
		return this.querySelector<HTMLElement>(
			`[data-role="toggle-filter"][data-path="${path}"]`
		);
	}

	getSearchInput(path: string) {
		if (!this._searchable) {
			return null;
		}

		return this.querySelector<HTMLInputElement>(
			`[data-role="search-input"][data-path="${path}"]`
		);
	}

	getCheckboxes(path: string) {
		return Array.from(
			this.querySelectorAll<HTMLInputElement>(
				`[data-group-path="${path}"] input[type="checkbox"]:not(:disabled)`
			)
		);
	}

	getFocusableElements(path: string) {
		const trigger = this.getTrigger(path);
		const searchInput = this.getSearchInput(path);
		return [
			...(trigger ? [trigger] : []),
			...(searchInput ? [searchInput] : []),
			...this.getCheckboxes(path),
		];
	}

	captureCurrentFocus(): PendingFocus | null {
		const activeElement = document.activeElement as HTMLElement | null;

		if (!activeElement || !this.contains(activeElement)) {
			return null;
		}

		if (activeElement.matches('[data-role="toggle-filter"]')) {
			return {
				type: 'trigger',
				path: activeElement.dataset.path || '',
			};
		}

		if (activeElement.matches('[data-role="search-input"]')) {
			return {
				type: 'search',
				path: activeElement.dataset.path || '',
			};
		}

		const checkbox: HTMLInputElement | null = activeElement.matches(
			'input[type="checkbox"]'
		)
			? (activeElement as HTMLInputElement)
			: (activeElement.querySelector(
					'input[type="checkbox"]'
			  ) as HTMLInputElement | null);

		if (!checkbox) {
			return null;
		}

		return {
			type: 'checkbox',
			path: checkbox.dataset.path || '',
			index: Number(checkbox.dataset.index),
		};
	}

	applyPendingFocus() {
		if (!this._pendingFocus) {
			return;
		}

		const { type, path, index } = this._pendingFocus;
		this._pendingFocus = null;

		if (type === 'trigger') {
			this.getTrigger(path)?.focus();
			return;
		}

		if (type === 'search') {
			this.getSearchInput(path)?.focus();
			return;
		}

		const checkboxes = this.getCheckboxes(path);
		const nextIndex =
			typeof index === 'number'
				? Math.max(0, Math.min(index, checkboxes.length - 1))
				: 0;
		checkboxes[nextIndex]?.focus();
	}

	escapeHtml(value: string) {
		const div = document.createElement('div');
		div.textContent = value;
		return div.innerHTML;
	}

	getVisibleBuckets(filter: AggregationsModel) {
		if (!this._searchable) {
			return filter.buckets.map((bucket, index) => ({ bucket, index }));
		}

		const normalizedSearch = this._searchTerm.trim().toLowerCase();
		const isOpenFilter = this._openPath === filter.path;

		return filter.buckets
			.map((bucket, index) => ({ bucket, index }))
			.filter(({ bucket }) => {
				if (!isOpenFilter || normalizedSearch === '') {
					return true;
				}

				return this.getBucketDisplayText(bucket)
					.toLowerCase()
					.includes(normalizedSearch);
			});
	}

	updateOpenGroupOptions() {
		if (!this._openPath) {
			return;
		}

		const filter = this.getFilterByPath(this._openPath);
		if (!filter) {
			return;
		}

		const optionsContainer = this.querySelector<HTMLElement>(
			`[data-role="options-container"][data-path="${filter.path}"]`
		);
		if (!optionsContainer) {
			return;
		}

		optionsContainer.innerHTML = this.renderOptions(filter);
		this.applyPendingFocus();
	}

	renderOptions(filter: AggregationsModel) {
		const buckets = this.getVisibleBuckets(filter);

		if (buckets.length === 0) {
			return '<div class="facet-multi-select-dropdown__empty-state">Inga alternativ matchar sökningen</div>';
		}

		return `
			<ul class="facet-multi-select-dropdown__options" role="listbox" aria-multiselectable="true">
				${buckets
					.map(({ bucket, index }) => {
						const count =
							bucket.count > 0
								? `<span class="facet-multi-select-dropdown__option-count">${bucket.count}</span>`
								: '';
						return `
							<li>
								<label class="facet-multi-select-dropdown__option ${
									bucket.disabled ? 'is-disabled' : ''
								}">
								<span class="facet-multi-select-dropdown__option-inner">
									<input
										type="checkbox"
										data-role="bucket-checkbox"
										data-path="${this.escapeHtml(filter.path)}"
										data-index="${index}"
										${bucket.selected ? 'checked' : ''}
										${bucket.disabled ? 'disabled' : ''}
									/>
									<span class="facet-multi-select-dropdown__option-text">${this.escapeHtml(
										this.getBucketDisplayText(bucket)
									)}</span>
									</span>
									${count}
								</label>
							</li>
						`;
					})
					.join('')}
			</ul>
		`;
	}

	renderGroup(filter: AggregationsModel) {
		const isOpen = this._openPath === filter.path;
		const selectedCount = filter.selectedBuckets;
		const searchValue =
			this._searchable && this._openPath === filter.path
				? this._searchTerm
				: '';

		return `
			<div class="facet-multi-select-dropdown__group" data-group-path="${this.escapeHtml(
				filter.path
			)}">
				<button
					type="button"
					class="facet-multi-select-dropdown__trigger"
					data-role="toggle-filter"
					data-path="${this.escapeHtml(filter.path)}"
					aria-expanded="${isOpen ? 'true' : 'false'}"
					aria-haspopup="listbox"
					aria-label="${this.escapeHtml(this._expandText)} ${this.escapeHtml(
			filter.name
		)}"
				>
					<span class="facet-multi-select-dropdown__trigger-text">${this.escapeHtml(
						filter.name
					)}</span>
					<span class="facet-multi-select-dropdown__trigger-meta">
						${
							selectedCount > 0
								? `<span class="facet-multi-select-dropdown__count">${selectedCount}</span>`
								: ''
						}
						<svg class="facet-multi-select-dropdown__icon ${
							isOpen ? 'open' : ''
						}" viewBox="0 0 20 20" fill="none" aria-hidden="true">
							<path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
						</svg>
					</span>
				</button>

				${
					isOpen
						? `
					<div class="facet-multi-select-dropdown__panel">
						<div class="facet-multi-select-dropdown__panel-inner">
							${
								this._searchable
									? `
								<input
									type="search"
									class="facet-multi-select-dropdown__search"
									data-role="search-input"
									data-path="${this.escapeHtml(filter.path)}"
									placeholder="Sök på ${this.escapeHtml(filter.name)}"
									value="${this.escapeHtml(searchValue)}"
								/>
							`
									: ''
							}
							<div class="facet-multi-select-dropdown__options-container" data-role="options-container" data-path="${this.escapeHtml(
								filter.path
							)}">
								${this.renderOptions(filter)}
							</div>
						</div>
					</div>
				`
						: ''
				}
			</div>
		`;
	}

	renderChips() {
		const selectedFilters = this.getSelectedFilters();

		if (selectedFilters.length === 0) {
			return '';
		}

		return `
			<div class="facet-multi-select-dropdown__chips">
				${selectedFilters
					.map(
						(item) => `
							<span class="facet-multi-select-dropdown__chip">
								<span class="sr-only">${this.escapeHtml(this._clearText)}</span>
								<span>${this.escapeHtml(item.bucketName)}</span>
								<button
									type="button"
									aria-label="${this.escapeHtml(this._clearText)} ${this.escapeHtml(
							item.bucketName
						)}"
									data-role="remove-chip"
									data-path="${this.escapeHtml(item.filterPath)}"
									data-index="${item.bucketIndex}"
								>
									<svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
										<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
									</svg>
								</button>
							</span>
						`
					)
					.join('')}
			</div>
		`;
	}

	render() {
		if (!this._connected) {
			return;
		}

		const filters = this._filters || [];
		if (!this._pendingFocus) {
			this._pendingFocus = this.captureCurrentFocus();
		}

		this.innerHTML = `
			<div class="facet-multi-select-dropdown__wrapper">
				<div class="facet-multi-select-dropdown__filters">
					${filters.map((filter) => this.renderGroup(filter)).join('')}
				</div>
				${this.renderChips()}
			</div>
		`;

		this.applyPendingFocus();
	}
}

if (typeof window !== 'undefined' && window.customElements) {
	if (!window.customElements.get('facet-multi-select-dropdown')) {
		window.customElements.define(
			'facet-multi-select-dropdown',
			FacetMultiSelectDropdownElement
		);
	}
}

export default FacetMultiSelectDropdownElement;
